<template>
  <div class="values-container relative overflow-hidden">
    <!-- Metal frame header with rivets and brushed texture -->
    <div 
      class="values-header relative overflow-hidden"
      ref="valuesHeader"
    >
      <div class="header-bg-texture"></div>
      <div class="header-frame">
        <div class="frame-rivet top-left"></div>
        <div class="frame-rivet top-right"></div>
        <div class="frame-rivet bottom-left"></div>
        <div class="frame-rivet bottom-right"></div>
      </div>
      
      <!-- Gradient overlay -->
      <div class="header-gradient-overlay"></div>
      
      <!-- Header content -->
      <div class="header-content relative z-10">
        <p 
          class="small-text" 
          :class="{'animate-in': isHeaderVisible}"
        >{{ firstLineText }}</p>
        <p 
          class="large-text" 
          :class="{'animate-in': isHeaderVisible}"
        >{{ secondLineText }}</p>
        
        <!-- Animated accent line -->
        <div 
          class="accent-line" 
          :class="{'animate-in': isHeaderVisible}"
        ></div>
      </div>
    </div>
    
    <!-- Values section with steel-inspired design -->
    <div class="values-grid">
      <!-- Dynamic value cards - iterate through our values -->
      <div 
        v-for="(value, index) in coreValues" 
        :key="value.title"
        class="value-section"
        :class="[index % 2 === 0 ? 'value-left' : 'value-right']"
        :ref="el => { valueSections[index] = el }"
        :id="getValueId(value.title)"
      >
        <!-- Steel plate with metallic texture -->
        <div 
          class="steel-plate"
          :style="{ '--delay': `${index * 0.1}s` }"
          @mousemove="e => handleMouseMove(e, index)"
          @mouseleave="resetParallax(index)"
        >
          <!-- Metallic corner accents -->
          <div class="corner-accent top-left"></div>
          <div class="corner-accent top-right"></div>
          <div class="corner-accent bottom-left"></div>
          <div class="corner-accent bottom-right"></div>
          
          <!-- Value title with updated styling -->
          <div class="value-title-container">
            <h2 
              class="value-title" 
              :ref="el => { valueTitles[index] = el }"
            >{{ value.title }}</h2>
          </div>
          
          <!-- Animated diagonal accent lines -->
          <div class="diagonal-accent left"></div>
          <div class="diagonal-accent right"></div>
          
          <!-- Glassmorphism content card -->
          <div 
            class="glass-content-card" 
            :class="{'active': activeIndex === index}"
            @mouseenter="activeIndex = index"
            @mouseleave="activeIndex = null"
          >
            <!-- Card gradient background -->
            <div class="card-gradient"></div>
            
            <!-- Content container -->
            <div class="content-wrapper">
              <p class="large-paragraph">{{ value.mainText }}</p>
              <p class="small-paragraph">{{ value.detailText }}</p>
              
              <!-- Conditional button for certain values -->
              <a 
                v-if="value.hasButton" 
                href="https://www.hendricksholding.com/"
                target="_blank"
                rel="noopener noreferrer"
                class="value-button group"
                @mouseenter="isButtonHovered = true"
                @mouseleave="isButtonHovered = false"
              >
                <span>Learn More About Hendricks Holding</span>
                <span class="arrow-icon">
                  <font-awesome-icon 
                    :icon="['fas', 'arrow-right']" 
                    class="transition-transform duration-300 group-hover:translate-x-1" 
                  />
                </span>
                <div class="button-bg"></div>
              </a>
            </div>
          </div>
          
          <!-- Parallax image container -->
          <div 
            class="image-container"
            :class="[`image-position-${index % 4}`]"
          >
            <!-- Main image with parallax effect -->
            <div 
              class="parallax-image" 
              :style="{ 
                backgroundImage: `url(${value.imageSrc})`,
                transform: `translate(${parallaxOffsets[index]?.x || 0}px, ${parallaxOffsets[index]?.y || 0}px)` 
              }"
            ></div>
            
            <!-- Decorative overlay elements -->
            <div class="image-overlay"></div>
            <div class="image-frame"></div>
            
            <!-- Metal accent elements -->
            <div class="metal-accent horizontal"></div>
            <div class="metal-accent vertical"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OurValues',
  data() {
    return {
      firstLineText: 'Understanding',
      secondLineText: 'Our Values',
      observers: [],
      isHeaderVisible: false,
      visibleSections: [],
      parallaxOffsets: [],
      activeIndex: null,
      isButtonHovered: false,
      valueSections: [], // Array to store value section refs
      valueTitles: [], // Array to store value title refs
      
      // Structured core values data
      coreValues: [
        {
          title: "RESPECT",
          mainText: "At Stainless Tank and Equipment, respect is the foundation of everything we do. We are committed to treating every associate, customer, and partner with honesty, integrity, and dignity. We believe that by fostering a culture of mutual respect, we create an environment where collaboration thrives, trust is built, and relationships are strengthened. This commitment extends beyond our internal team to the customers and communities we serve, ensuring every interaction reflects the high regard we have for others.",
          detailText: "Respect at our company means recognizing the unique potential and contributions of every individual. Whether it's a new associate discovering their role, a seasoned professional sharing insights, or a customer sharing feedback, we actively listen and value diverse perspectives. For instance, our mentorship program pairs experienced team members with newer employees, encouraging knowledge sharing and growth. We also prioritize open communication, where every voice is heard and valued. By embracing respect in our daily operations, we not only drive long-term success for our team and clients but also create a workplace and community where everyone feels valued and empowered to excel.",
          imageSrc: "/img/values/respect.png",
          hasButton: false
        },
        {
          title: "OPPORTUNITY",
          mainText: "At Stainless Tank and Equipment, we firmly believe in creating opportunities that empower individuals to achieve their full potential. Everyone deserves a fair chance to pursue their dreams, and we take pride in providing the tools, resources, and support necessary to make that possible. From professional development programs to pathways for personal growth, we're dedicated to fostering an environment where ambition and hard work lead to success.",
          detailText: "Creating opportunities is more than just a corporate philosophy—it's an actionable commitment. We've implemented initiatives such as tailored training programs, scholarship offerings, and internal promotions to ensure our associates can thrive in their roles and beyond. Additionally, we extend this ethos to our community by partnering with local organizations to create internships and apprenticeships that introduce new talent to the industry. For example, a recent collaboration with a local technical college led to several students gaining hands-on experience in advanced manufacturing, many of whom are now valued team members. By championing opportunity, we are not only enriching lives but also building a stronger, more dynamic future for our industry and community.",
          imageSrc: "/img//values/opportunity.png",
          hasButton: false
        },
        {
          title: "WORK HARD / HAVE FUN",
          mainText: "At Stainless Tank and Equipment, we value the passion and dedication that our associates bring to their work. We recognize that hard work is the cornerstone of growth and achievement, and we celebrate the efforts of those who push themselves to reach their full potential. At the same time, we believe that a positive and enjoyable workplace fosters creativity, collaboration, and a sense of community among our team members.",
          detailText: "Balancing hard work with fun is part of our company culture. From team-building events to workplace celebrations, we create opportunities for our associates to connect and recharge. Whether it's a lighthearted competition during a holiday party or sharing laughs over a team lunch, these moments of enjoyment strengthen bonds and boost morale. Our commitment to this balance ensures that our workplace remains dynamic, engaging, and fulfilling for everyone who is part of our team.",
          imageSrc: "/img/values/workhard.png",
          hasButton: true
        },
        {
          title: "ENTREPRENEURIAL SPIRIT",
          mainText: "At Stainless Tank and Equipment, we are driven by an entrepreneurial spirit that inspires us to continuously improve and innovate. We encourage every associate to think critically, imagine boldly, and approach challenges with determination. This spirit fuels our ability to adapt to changing markets, embrace new ideas, and deliver value to our customers in creative and effective ways.",
          detailText: "Fostering an entrepreneurial mindset means empowering our team at all levels to contribute ideas and take initiative. For example, our innovation workshops provide a platform for associates to collaborate on solutions that improve efficiency and enhance product quality. We also celebrate and reward ingenuity, such as when a recent process improvement suggested by a team member significantly reduced production time without compromising quality. By cultivating this spirit of innovation, we ensure that our company remains forward-thinking, competitive, and poised for long-term success.",
          imageSrc: "/img/values/entrepreneurialspirit.png",
          hasButton: false
        },
        {
          title: "FAMILY",
          mainText: "At Stainless Tank and Equipment, we consider ourselves more than just a team; we are a family. This sense of family extends beyond organizational relationships to include our associates, their loved ones, and the customers we serve. We trust and rely on one another, creating a supportive and collaborative environment that empowers everyone to succeed.",
          detailText: "Caring for our family means prioritizing well-being and fostering strong connections. We offer initiatives such as flexible work schedules and wellness programs to help associates balance their personal and professional lives. Additionally, our community outreach events often involve families, strengthening bonds and fostering a sense of unity. For example, our annual family day brings associates and their loved ones together for a day of fun, food, and activities, reinforcing the importance of connection and trust within our company culture.",
          imageSrc: "/img/values/family.png",
          hasButton: false
        },
        {
          title: "GIVE BACK",
          mainText: "Gratitude is at the heart of everything we do at Stainless Tank and Equipment. We believe in giving back to the communities that have supported us by sharing our time, resources, and expertise. This commitment to generosity not only strengthens our communities but also enriches the lives of our associates.",
          detailText: "Our dedication to giving back is reflected in initiatives like volunteer days, charitable donations, and partnerships with local organizations. For instance, we recently collaborated with a local food bank to organize a company-wide food drive, collecting thousands of meals for families in need. Additionally, our associates are encouraged to participate in paid volunteer hours, allowing them to contribute to causes they are passionate about. By giving back, we aim to create a positive impact that extends far beyond our organization and inspires others to do the same.",
          imageSrc: "/img/values/giveback.png",
          hasButton: false
        },
        {
          title: "AMERICAN PRIDE",
          mainText: "At Stainless Tank and Equipment, we take immense pride in being part of a nation built on perseverance, innovation, and the promise of opportunity. The American Dream resonates deeply within our company, driving us to uphold the values of hard work, determination, and progress. We honor the spirit of our country by crafting high-quality products that contribute to industries essential to our nation's growth and well-being.",
          detailText: "Our commitment to American pride extends beyond our operations. We express deep gratitude for the men and women who serve and protect our country, recognizing their sacrifices as the cornerstone of our freedoms. To show our support, we actively engage in initiatives like hiring veterans, participating in local patriotic events, and supporting organizations that aid military families. Our dedication to these principles is a testament to our belief in the power of unity, respect, and shared purpose—values that define both our company and the nation we are proud to call home.",
          imageSrc: "/img/values/americanpride.png",
          hasButton: false
        }
      ]
    }
  },
  mounted() {
    // Initialize arrays for section refs
    this.valueSections = new Array(this.coreValues.length).fill(null);
    this.valueTitles = new Array(this.coreValues.length).fill(null);
    
    // Initialize parallax offsets for each value section
    this.parallaxOffsets = this.coreValues.map(() => ({ x: 0, y: 0 }));
    
    // Wait for DOM to be ready
    this.$nextTick(() => {
      this.setupIntersectionObservers();
    });
  },
  beforeUnmount() {
    // Clean up observers
    this.observers.forEach(observer => observer.disconnect());
  },
  methods: {
    setupIntersectionObservers() {
      // Options for header observer
      const headerOptions = {
        threshold: 0.2,
        rootMargin: '-50px 0px'
      };
      
      // Observer for header section
      const headerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.isHeaderVisible = true;
          }
        });
      }, headerOptions);
      
      // Observe header
      if (this.$refs.valuesHeader) {
        headerObserver.observe(this.$refs.valuesHeader);
        this.observers.push(headerObserver);
      }
      
      // Options for value sections observer
      const sectionOptions = {
        threshold: 0.15,
        rootMargin: '-100px 0px'
      };
      
      // Observer for value sections
      const sectionsObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          // Get the index of the observed section
          const index = this.valueSections.indexOf(entry.target);
          
          if (index !== -1) {
            if (entry.isIntersecting) {
              // Add to visible sections
              if (!this.visibleSections.includes(index)) {
                this.visibleSections.push(index);
                entry.target.classList.add('in-view');
              }
            }
          }
        });
      }, sectionOptions);
      
      // Observe all value sections
      this.valueSections.forEach(section => {
        if (section) {
          sectionsObserver.observe(section);
        }
      });
      
      this.observers.push(sectionsObserver);
    },
    
    handleMouseMove(e, index) {
      // Don't process if we don't have the section
      if (!this.valueSections[index]) return;
      
      const section = this.valueSections[index];
      const rect = section.getBoundingClientRect();
      
      // Calculate mouse position relative to the center of the element
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Calculate the difference from center (normalized from -1 to 1)
      const offsetX = (mouseX - centerX) / centerX;
      const offsetY = (mouseY - centerY) / centerY;
      
      // Apply parallax effect to image
      if (this.parallaxOffsets && this.parallaxOffsets[index]) {
        this.parallaxOffsets[index] = {
          x: offsetX * -20, // Move opposite to mouse movement
          y: offsetY * -15
        };
      }
    },
    
    resetParallax(index) {
      // Reset parallax effect
      if (this.parallaxOffsets && this.parallaxOffsets[index]) {
        this.parallaxOffsets[index] = { x: 0, y: 0 };
      }
    },

    /**
     * Get the kebab-case ID for a value section
     * @param {string} title - The title of the value
     * @returns {string} - The kebab-case ID with 'value-' prefix
     */
    getValueId(title) {
      return `value-${title
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/\//g, '-')}`; // Handle values with slashes
    },
  }
};
</script>

<style scoped>
/* Main container */
.values-container {
  width: 100%;
  overflow: hidden;
  position: relative;
}

/* Add subtle metal texture to background */
.values-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23b7b9ba' fill-opacity='0.03'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  pointer-events: none;
}

/* Header Styling */
.values-header {
  text-align: center;
  margin-bottom: 4rem;
  padding: 3rem 2rem;
  position: relative;
}

.header-bg-texture {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    repeating-linear-gradient(
      -45deg,
      rgba(183, 185, 186, 0.03) 0px,
      rgba(183, 185, 186, 0.03) 1px,
      transparent 1px,
      transparent 11px
    ),
    repeating-linear-gradient(
      45deg,
      rgba(183, 185, 186, 0.03) 0px,
      rgba(183, 185, 186, 0.03) 1px,
      transparent 1px,
      transparent 11px
    );
  opacity: 0.4;
  pointer-events: none;
}

.header-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 1px solid rgba(183, 185, 186, 0.1);
  pointer-events: none;
}

.frame-rivet {
  position: absolute;
  width: 12px;
  height: 12px;
  background: radial-gradient(circle, rgba(143, 0, 0, 0.9) 0%, rgba(83, 0, 0, 0.8) 60%, rgba(20, 0, 0, 0.8) 100%);
  border-radius: 50%;
  box-shadow: 
    inset 0 1px 2px rgba(255, 255, 255, 0.3),
    0 1px 2px rgba(0, 0, 0, 0.5);
}

.frame-rivet.top-left {
  top: 10px;
  left: 10px;
}

.frame-rivet.top-right {
  top: 10px;
  right: 10px;
}

.frame-rivet.bottom-left {
  bottom: 10px;
  left: 10px;
}

.frame-rivet.bottom-right {
  bottom: 10px;
  right: 10px;
}

.header-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(143, 0, 0, 0.1) 0%, transparent 50%, rgba(143, 0, 0, 0.05) 100%);
  pointer-events: none;
}

.header-content {
  position: relative;
}

.small-text {
  font-size: 1.5rem;
  color: rgba(183, 185, 186, 0.8);
  margin-bottom: 0.5rem;
  transform: translateY(30px);
  opacity: 0;
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.large-text {
  font-size: 3.5rem;
  color: white;
  font-weight: bold;
  margin-bottom: 1rem;
  transform: translateY(30px);
  opacity: 0;
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s, opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s;
}

.accent-line {
  width: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, rgba(143, 0, 0, 0.8), rgba(183, 185, 186, 0.8), rgba(143, 0, 0, 0.8), transparent);
  margin: 1.5rem auto;
  opacity: 0;
  transition: width 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.4s, opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s;
}

/* Animation states for header elements */
.small-text.animate-in,
.large-text.animate-in {
  transform: translateY(0);
  opacity: 1;
}

.accent-line.animate-in {
  width: 50%;
  max-width: 500px;
  opacity: 1;
}

/* Values Grid Layout */
.values-grid {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8rem;
  padding: 0 2rem;
  max-width: 1800px;
  margin: 0 auto;
}

/* Value Section Styling */
.value-section {
  width: 100%;
  position: relative;
  opacity: 0;
  transform: translateY(60px);
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);
  perspective: 1000px;
}

.value-section.in-view {
  opacity: 1;
  transform: translateY(0);
}

/* Alternate layout - left and right */
.value-left .steel-plate {
  margin-left: 0;
  margin-right: auto;
  clip-path: polygon(0 0, 100% 5%, 95% 100%, 0% 95%);
}

.value-right .steel-plate {
  margin-left: auto;
  margin-right: 0;
  clip-path: polygon(5% 0, 100% 0%, 100% 95%, 0% 100%);
}

/* Steel plate styling */
.steel-plate {
  position: relative;
  width: 90%;
  max-width: 1400px;
  background: linear-gradient(135deg, #2a2b38 0%, #22232c 50%, #1d1e26 100%);
  border-radius: 4px;
  padding: 3rem;
  box-shadow: 
    0 20px 50px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  overflow: hidden;
  transition: all 0.5s ease;
  transform: translateZ(0) translateY(var(--translate-y, 0));
  will-change: transform;
}

.steel-plate::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%, rgba(255, 255, 255, 0.03) 100%),
    repeating-linear-gradient(
      45deg,
      rgba(183, 185, 186, 0.01) 0px,
      rgba(183, 185, 186, 0.01) 1px,
      transparent 1px,
      transparent 5px
    );
  pointer-events: none;
}

/* Corner accents */
.corner-accent {
  position: absolute;
  width: 30px;
  height: 30px;
  border-color: rgba(143, 0, 0, 0.7);
  z-index: 2;
}

.corner-accent.top-left {
  top: 10px;
  left: 10px;
  border-top: 2px solid;
  border-left: 2px solid;
}

.corner-accent.top-right {
  top: 10px;
  right: 10px;
  border-top: 2px solid;
  border-right: 2px solid;
}

.corner-accent.bottom-left {
  bottom: 10px;
  left: 10px;
  border-bottom: 2px solid;
  border-left: 2px solid;
}

.corner-accent.bottom-right {
  bottom: 10px;
  right: 10px;
  border-bottom: 2px solid;
  border-right: 2px solid;
}

/* Metallic value title styling */
.value-title-container {
  position: relative;
  margin-bottom: 2rem;
  overflow: hidden;
}

.value-title {
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--color-base-content);
  -webkit-text-stroke: 1px var(--color-base-300);
  position: relative;
  z-index: 2;
  margin: 0;
  line-height: 1.1;
}

.title-reflection {
  position: absolute;
  bottom: -15px;
  left: 0;
  width: 100%;
  height: 15px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), transparent);
  transform: scaleY(-1);
  opacity: 0.3;
  filter: blur(1px);
}

/* Diagonal accent lines */
.diagonal-accent {
  position: absolute;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(143, 0, 0, 0.5), rgba(183, 185, 186, 0.5), transparent);
  z-index: 1;
  transform-origin: top left;
}

.diagonal-accent.left {
  width: 50%;
  top: 0;
  left: 0;
  transform: rotate(-25deg) translateY(100px);
}

.diagonal-accent.right {
  width: 50%;
  bottom: 0;
  right: 0;
  transform: rotate(-25deg) translateY(-100px);
}

/* Glass content card */
.glass-content-card {
  position: relative;
  width: 65%;
  background: rgba(30, 31, 40, 0.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  transition: all 0.4s ease;
  z-index: 5;
}

.value-left .glass-content-card {
  margin-left: auto;
  margin-right: 5%;
}

.value-right .glass-content-card {
  margin-left: 5%;
  margin-right: auto;
}

.glass-content-card.active {
  transform: translateY(-5px);
  box-shadow: 
    0 15px 40px rgba(0, 0, 0, 0.3),
    0 0 15px rgba(143, 0, 0, 0.2);
}

.card-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(143, 0, 0, 0.1) 0%, transparent 50%, rgba(143, 0, 0, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.5s ease;
  pointer-events: none;
}

.glass-content-card.active .card-gradient {
  opacity: 1;
}

.content-wrapper {
  position: relative;
  z-index: 2;
}

/* Text content */
.large-paragraph {
  font-size: 1.25rem;
  line-height: 1.6;
  color: var(--color-secondary);
  margin-bottom: 1.5rem;
}

.small-paragraph {
  font-size: 1rem;
  line-height: 1.5;
  color: var(--color-base-content);
}

/* Button styling */
.value-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: white;
  border: 1px solid rgba(143, 0, 0, 0.7);
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  overflow: hidden;
  margin-top: 1.5rem;
  transition: all 0.3s ease;
}

.button-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(143, 0, 0, 0.8);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
  z-index: -1;
}

.value-button:hover .button-bg {
  transform: scaleX(1);
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.value-button:hover .arrow-icon {
  transform: translateX(4px);
}

/* Image container */
.image-container {
  position: absolute;
  width: 45%;
  height: 70%;
  max-height: 450px;
  z-index: 1;
  overflow: hidden;
  border-radius: 6px;
  box-shadow: 
    10px 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.05);
}

/* Image position variations for visual interest */
.image-position-0 {
  top: 30%;
  left: 0;
}

.image-position-1 {
  top: 30%;
  right: 0;
}

.image-position-2 {
  top: 30%;
  left: 0;
}

.image-position-3 {
  top: 30%;
  right: 0;
}

.parallax-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 0.2s ease-out;
  will-change: transform;
  transform: scale(1.1); /* Slightly larger to allow for movement */
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.192) 0%, rgba(0, 0, 0, 0.4) 100%);
  mix-blend-mode: multiply;
  pointer-events: none;
}

.image-frame {
  position: absolute;
  top: 6px;
  left: 6px;
  right: 6px;
  bottom: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  pointer-events: none;
}

/* Metal accents on images */
.metal-accent {
  position: absolute;
  background: linear-gradient(90deg, rgba(143, 0, 0, 0.7), rgba(83, 0, 0, 0.7));
  pointer-events: none;
}

.metal-accent.horizontal {
  height: 3px;
  width: 40%;
  top: 20px;
  right: 20px;
}

.metal-accent.vertical {
  width: 3px;
  height: 40%;
  bottom: 20px;
  left: 20px;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .glass-content-card {
    width: 75%;
  }
  
  .value-title {
    font-size: 3.5rem;
  }
  
  .large-paragraph {
    font-size: 1.125rem;
  }
}

@media (max-width: 992px) {
  .values-grid {
    gap: 5rem;
  }
  
  .glass-content-card {
    width: 85%;
    padding: 2rem;
  }
  
  .image-container {
    width: 40%;
    height: 50%;
  }
  
  .value-title {
    font-size: 3rem;
  }
}

@media (max-width: 768px) {
  .values-header {
    margin-top: 10vh;
    margin-bottom: 4rem;
    padding: 2rem 1rem;
  }
  
  .small-text {
    font-size: 1.25rem;
  }
  
  .large-text {
    font-size: 2.5rem;
  }
  
  .steel-plate {
    width: 100%;
    padding: 2rem 1.5rem;
    clip-path: none !important;
  }
  
  .glass-content-card {
    width: 100%;
    margin: 0 !important;
  }
  
  .value-title {
    font-size: 2.5rem;
    text-align: center;
  }
  
  .image-container {
    position: relative;
    width: 100%;
    height: 250px;
    margin-bottom: 2rem;
    top: auto !important;
    left: auto !important;
    right: auto !important;
    bottom: auto !important;
  }
  
  .large-paragraph, .small-paragraph {
    font-size: 1rem;
  }
  
  .values-grid {
    padding: 0 1rem;
    gap: 4rem;
  }
}

/* Hardware acceleration optimizations */
.parallax-image,
.value-title,
.glass-content-card,
.steel-plate {
  will-change: transform;
}

/* Entrance animations - controlled via JavaScript */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>